"use client"

import { useState, useRef, useCallback } from "react"
import DisplayPrice from "@modules/common/components/display-price"
import { VariantPrice } from "types/global"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import WishlistButton from "@modules/common/components/wishlist-button"
import ViewAllLink from "@modules/common/components/view-all-link"
import { getProductUrl } from "@lib/util/product"

type ProductData = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  /** Price OBJECT so both charge and display currency can render (#166532). */
  price: VariantPrice | null
  badge: string | null
}

type TabData = {
  id: string
  label: string
  products: ProductData[]
}

function ProductImage({
  product,
  wrapperClassName,
  imageClassName,
  fallbackBg,
  badge,
  wishlist,
}: {
  product: ProductData
  wrapperClassName: string
  imageClassName: string
  fallbackBg: string
  badge?: boolean
  wishlist?: boolean
}) {
  const [imgError, setImgError] = useState(false)
  return (
    <div className={wrapperClassName}>
      {product.thumbnail && !imgError ? (
        <Image
          src={product.thumbnail}
          alt={product.title}
          fill
          className={imageClassName}
          sizes="(max-width: 768px) 45vw, 22vw"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className={`absolute inset-0 flex items-center justify-center ${fallbackBg}`}>
          <svg className="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      {badge && product.badge && (
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-semibold bg-brand-hero-navy text-white leading-tight">
          {product.badge}
        </span>
      )}
      {wishlist && <WishlistButton productId={product.id} productTitle={product.title} />}
    </div>
  )
}

function ProductCard({ product, style = "style1" }: { product: ProductData; style?: "style1" | "style2" }) {
  const s1 = style === "style1"
  return (
    <LocalizedClientLink href={getProductUrl(product.handle)} className="group block">
      <ProductImage
        product={product}
        wrapperClassName={`relative aspect-[4/5] mb-3 bg-white ${s1 ? "rounded-lg" : ""} overflow-hidden`}
        imageClassName={`object-contain ${s1 ? "p-3 " : ""}group-hover:scale-105 transition-transform duration-300`}
        fallbackBg={s1 ? "bg-white" : "bg-gray-50"}
        badge={s1}
        wishlist={s1}
      />
      <h3 className={`text-xs md:text-sm ${s1 ? "font-medium" : "font-semibold"} text-gray-900 line-clamp-2 ${s1 ? "mb-1.5" : "mb-1"} min-h-[32px] md:min-h-[40px]`}>
        {product.title}
      </h3>
      {product.price && (
        <p className={`text-sm font-bold text-gray-900 ${s1 ? "mb-3" : ""}`}>
          <DisplayPrice
            amount={product.price.calculated_price_number}
            currencyCode={product.price.currency_code}
            data-testid="price"
          />
        </p>
      )}
      {s1 && product.price && (
        <span className="block w-full py-2.5 text-sm font-semibold text-center rounded-full bg-brand-hero-navy text-white group-hover:bg-brand-primary-blue transition-colors">
          Add to cart
        </span>
      )}
    </LocalizedClientLink>
  )
}

export default function PageBlockCarousel({
  title,
  subtitle,
  tabs,
  cardStyle = "style1",
  bgClass = "bg-[#f7f8fa]",
  backgroundColor,
}: {
  title: string
  subtitle?: string | null
  tabs: TabData[]
  cardStyle?: "style1" | "style2"
  bgClass?: string
  backgroundColor?: string | null
}) {
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? "")
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = useCallback((direction: "left" | "right") => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollRef.current.clientWidth * 0.8 : scrollRef.current.clientWidth * 0.8,
      behavior: "smooth",
    })
  }, [])

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0]
  if (!activeTab || activeTab.products.length === 0) return null

  return (
    <section
      className={`py-[30px] md:py-10 ${backgroundColor ? "" : bgClass}`}
      style={backgroundColor ? { backgroundColor } : undefined}
      aria-labelledby={`block-heading-${tabs[0].id}`}
    >
      <div className="max-w-screen-xl mx-auto px-4">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 md:gap-4">
              <h2
                id={`block-heading-${tabs[0].id}`}
                className="text-[22px] md:text-[36px] font-black text-brand-navy"
              >
                {title}
              </h2>
              <div className="hidden md:block">
                <ViewAllLink href="/store" />
              </div>
            </div>
            {subtitle && <p className="text-sm text-brand-body mt-1">{subtitle}</p>}
          </div>
          <div className="hidden md:flex gap-2 ml-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:border-brand-navy hover:text-brand-navy transition-colors"
              aria-label="Scroll left"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-full border border-brand-navy flex items-center justify-center text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
              aria-label="Scroll right"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="flex items-center gap-2 mb-5 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTabId(tab.id)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  tab.id === activeTabId
                    ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-white text-gray-700 border-gray-300 hover:border-brand-navy hover:text-brand-navy"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2"
        >
          {activeTab.products.map((product) => (
            <div
              key={product.id}
              className="flex-none w-[calc((100%-32px)/2)] md:w-[calc((100%-64px)/5)] snap-start"
            >
              <ProductCard product={product} style={cardStyle} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
